import React from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { t } from "i18next";
import AppButton from "@/features/line-sheet-sets/app-button";
import BrandRequestModal from "@/shared/brand-request-modal";
import { useSearchParams } from "react-router-dom";
import CustomIcon from "../../../public/icons-v1_1";

interface Props {
  isBrandFiltered?: boolean;
  handleResetFilter?: () => void;
  isOrderHistory?: boolean;
}

export default function NoResults({
  isBrandFiltered,
  handleResetFilter,
  isOrderHistory,
}: Props) {
  const { t, tTitle, tSentence } = useI18nHelper();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchParams] = useSearchParams();
  const statusGroup = searchParams.get("status_group__eq");

  // 1. 허용된 키 리스트 정의
  const allowedKeys = [
    "status_group__eq",
    "page_number",
    "line_sheet_set.type__eq",
  ];

  // 2. 현재 searchParams에 담긴 모든 키 추출
  const currentKeys = Array.from(searchParams.keys());

  // 3. 허용되지 않은 키가 하나라도 있는지 검사
  const hasExtraParams = currentKeys.some((key) => !allowedKeys.includes(key));

  // 4. 결과: extraParams가 없고, 필수 파라미터가 있는지 확인
  const isDefaultState = !hasExtraParams;

  if (statusGroup && isDefaultState) {
    return (
      <Flex justifyContent={"center"} alignItems={"center"} height={"600px"}>
        <Text textStyle={"body-lg-regular"} color={"#444444"}>
          {tSentence(
            "order_history:no_result_msg." + statusGroup.toLowerCase()
          )}
        </Text>
      </Flex>
    );
  }

  if (isOrderHistory) {
    return (
      <Flex
        gap={"20px"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"600px"}
        flexDirection={"column"}
      >
        <Flex
          gap={"16px"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
        >
          <CustomIcon
            src={"warning-triangle-solid"}
            width={48}
            height={48}
            color={"#D4D4D1"}
          />
          <Text textStyle={"body-lg-regular"} color={"#444444"}>
            {tTitle("no_results")}
          </Text>
        </Flex>
        <AppButton
          key={"AgencyLineSheetSetListFilter_ResetFilter"}
          size={"medium"}
          onClick={handleResetFilter}
          width={"85px"}
          height={"28px"}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <SVG
              src={"/icons-v1_1/arrow-rotate.svg"}
              width={"16px"}
              height={"16px"}
              color={"#8F8F8C"}
            />
            <Text textStyle="body-sm-bold" color={"#444444"}>
              {t("clear_all")}
            </Text>
          </Flex>
        </AppButton>
      </Flex>
    );
  }

  return (
    <Box
      width={"100%"}
      height={"100%"}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      pointerEvents={"auto"}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        gap={"16px"}
      >
        <SVG
          src={"/icons-v1_1/warning-triangle-solid.svg"}
          width={"48px"}
          height={"48px"}
          color={"#D4D4D1"}
        />
        <Flex flexDirection={"column"} alignItems={"center"} gap={"4px"}>
          <Text color={"#444444"}>{tSentence("no_results")}</Text>
          {isBrandFiltered && <Text>{tSentence("request_brand_or_sku")}</Text>}
        </Flex>
        <Flex gap={"12px"}>
          <AppButton
            key={"AgencyLineSheetSetListFilter_ResetFilter"}
            size={"medium"}
            onClick={handleResetFilter}
            width={"85px"}
            height={"28px"}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <SVG
                src={"/icons-v1_1/arrow-rotate.svg"}
                width={"16px"}
                height={"16px"}
                color={"#8F8F8C"}
              />
              <Text textStyle="body-sm-bold" color={"#444444"}>
                {t("clear_all")}
              </Text>
            </Flex>
          </AppButton>
          {isBrandFiltered && (
            <AppButton
              size={"medium"}
              width={"85px"}
              height={"28px"}
              onClick={() => setIsOpen(true)}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <SVG
                  src={"/icons-v1_1/plus.svg"}
                  width={"16px"}
                  height={"16px"}
                  color={"#8F8F8C"}
                />
                <Text textStyle="body-sm-bold" color={"#444444"}>
                  {tTitle("request_no_result")}
                </Text>
              </Flex>
            </AppButton>
          )}
        </Flex>
      </Box>
      <BrandRequestModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </Box>
  );
}
